import {
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
} from '@mui/material';
import {
    RecordContextProvider,
    SimpleListLoading,
    useListContext,
} from 'react-admin';
import { Link } from 'react-router-dom';

import { formatRelativeBackendDate } from '@/utils/date_utils';
import { ticketStatuses } from '@/root/defaultConfiguration';
import { Ticket } from '@/types';

const EXCERPT_LEN = 100;

const excerpt = (content: string) => {
    const text = content.replace(/\s+/g, ' ').trim();
    if (text.length <= EXCERPT_LEN) return text;
    return `${text.slice(0, EXCERPT_LEN - 1)}…`;
};

const resolvedRowSx = {
    bgcolor: 'success.50',
    borderLeft: 3,
    borderColor: 'success.main',
    '&:hover': { bgcolor: 'success.100' },
} as const;

export const TicketListContent = () => {
    const { data: tickets, error, isPending } = useListContext<Ticket>();
    if (isPending) {
        return <SimpleListLoading hasSecondaryText />;
    }
    if (error) {
        return null;
    }

    const now = Date.now();

    return (
        <List dense>
            {tickets.map(item => (
                <RecordContextProvider key={item.id} value={item}>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            to={`/tickets/${item.id}/show`}
                            sx={
                                item.status === ticketStatuses.resolved
                                    ? resolvedRowSx
                                    : undefined
                            }
                        >
                            <ListItemText
                                primary={excerpt(item.content)}
                                secondary={
                                    [
                                        item.created_by_name || 'Unknown',
                                        item.created_at
                                            ? formatRelativeBackendDate(
                                                  item.created_at,
                                                  now
                                              )
                                            : null,
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')
                                }
                                primaryTypographyProps={{
                                    variant: 'body1',
                                }}
                                secondaryTypographyProps={{
                                    variant: 'caption',
                                }}
                            />
                            {item.status === ticketStatuses.resolved && (
                                <Typography
                                    variant="caption"
                                    color="success.main"
                                    sx={{ ml: 1, whiteSpace: 'nowrap' }}
                                >
                                    Resolved
                                </Typography>
                            )}
                        </ListItemButton>
                    </ListItem>
                </RecordContextProvider>
            ))}

            {tickets.length === 0 && (
                <ListItem>
                    <ListItemText primary="No tickets found" />
                </ListItem>
            )}
        </List>
    );
};
